<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SearchInput from 'dashboard/routes/dashboard/comments/components/SearchInput.vue';
import Icon from 'next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const getters = useStoreGetters();
const searchTerm = ref('');
const isFilterPanelOpen = ref(false);
const orderBy = ref('');
const statusFilters = ref({
  active: false,
  completed: false,
  draft: false,
});
const selectedCampaign = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const currentPage = ref(1);
const perPage = 10;

const uiFlags = useMapGetter('campaigns/getUIFlags');
const campaignsMeta = useMapGetter('campaigns/getMeta');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasActiveStatusFilter = computed(() => {
  return (
    statusFilters.value.active ||
    statusFilters.value.completed ||
    statusFilters.value.draft
  );
});

const totalPages = computed(() => campaignsMeta.value?.totalPages || 1);
const totalCount = computed(() => campaignsMeta.value?.totalCount || 0);

let searchDebounceTimeout = null;

const fetchCampaigns = () => {
  const activeStatuses = [];
  if (statusFilters.value.active) activeStatuses.push('active');
  if (statusFilters.value.draft) activeStatuses.push('draft');
  if (statusFilters.value.completed) {
    activeStatuses.push('completed');
    activeStatuses.push('running');
  }

  const params = {
    page: currentPage.value,
    per_page: perPage,
  };

  if (searchTerm.value) {
    params.search = searchTerm.value;
  }

  if (activeStatuses.length > 0) {
    params.status = activeStatuses;
  }

  if (orderBy.value) {
    params.order_by = orderBy.value;
  }

  store.dispatch('campaigns/get', params);
};

const handleSearch = value => {
  searchTerm.value = value;
  currentPage.value = 1;
  clearTimeout(searchDebounceTimeout);
  searchDebounceTimeout = setTimeout(() => {
    fetchCampaigns();
  }, 500);
};

function openFilterPanel() {
  isFilterPanelOpen.value = true;
}

function closeFilterPanel() {
  isFilterPanelOpen.value = false;
}

function applyFiltersAndFetch() {
  currentPage.value = 1;
  fetchCampaigns();
}

function applyFilters() {
  applyFiltersAndFetch();
  closeFilterPanel();
}

function clearFilters() {
  statusFilters.value = { active: false, completed: false, draft: false };
  orderBy.value = '';
  searchTerm.value = '';
  applyFiltersAndFetch();
}

function handleCreateCampaign() {
  router.push({ name: 'campaigns_whatsapp_new' });
}

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleEdit = campaign => {
  const campaignId = campaign.display_id || campaign.id;

  if (!campaignId) {
    useAlert(t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE'));
    return;
  }

  router.push({
    name: 'campaigns_whatsapp_edit',
    params: { id: campaignId },
  });
};

const handleDuplicate = async campaign => {
  try {
    const inboxId = campaign.inbox_id || campaign.inbox?.id;

    if (!inboxId) {
      useAlert(t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE'));
      return;
    }

    const duplicatedCampaignData = {
      campaign: {
        title: `${campaign.title} (Cópia)`,
        inbox_id: Number(inboxId),
        message: campaign.message,
        campaign_type: campaign.campaign_type,
        enabled: false,
        campaign_status: 'draft',
        audience: campaign.audience || [],
        template_params: campaign.template_params || {},
        description: campaign.description || '',
      },
    };

    await store.dispatch('campaigns/create', duplicatedCampaignData);

    fetchCampaigns();

    useAlert(t('CAMPAIGN.ACTIONS.DUPLICATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.ERROR_MESSAGE'));
  }
};

const handleViewReport = campaign => {
  const campaignId = campaign.display_id || campaign.id;

  if (!campaignId) {
    useAlert(t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE'));
    return;
  }

  router.push({
    name: 'campaign_report',
    params: { campaignId },
  });
};

const handleViewDetails = campaign => {
  const campaignId = campaign.display_id || campaign.id;

  if (!campaignId) {
    useAlert(t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE'));
    return;
  }

  router.push({
    name: 'campaigns_whatsapp_view',
    params: { id: campaignId },
  });
};

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

watch(currentPage, fetchCampaigns);

onMounted(fetchCampaigns);
</script>

<template>
  <div class="flex flex-col h-full">
    <header
      class="flex flex-col px-4 pt-2 bg-white dark:bg-n-black/30 border-b border-slate-50 dark:border-n-weak"
    >
      <div class="flex items-center gap-2 mb-6">
        <SearchInput
          class="flex-1"
          search-placeholder="Busca de campanhas"
          :search-term="searchTerm"
          @update:search-term="handleSearch"
        />
        <button
          class="ml-2 flex flex-row items-center justify-center w-11 h-11 rounded-lg bg-slate-100 dark:bg-n-solid-2 py-1.5 px-2.5 gap-2.5 hover:bg-slate-200 dark:hover:bg-n-solid-3 transition-colors"
          type="button"
          @click="openFilterPanel"
        >
          <Icon
            icon="i-lucide-arrow-up-down"
            class="text-slate-500 dark:text-n-slate-10"
            size="24"
          />
        </button>
      </div>
      <div class="flex justify-between items-center mb-8 mt-2">
        <h1
          class="flex items-center mb-0 text-2xl text-slate-900 dark:text-n-slate-1"
        >
          <span class="text-2xl font-medium text-slate-900 dark:text-n-slate-1">
            {{ t('CAMPAIGN.WHATSAPP.HEADER_TITLE') }}
          </span>
        </h1>
        <Button
          :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
          icon="i-lucide-plus"
          size="sm"
          class="group-hover/campaign-button:brightness-110"
          @click="handleCreateCampaign"
        />
      </div>
    </header>
    <main
      class="flex-1 overflow-auto flex-col px-4 pt-2 pb-10 bg-white dark:bg-n-black/30 border-b border-slate-50 dark:border-n-weak"
    >
      <div
        v-if="isFetchingCampaigns"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <template v-else-if="WhatsAppCampaigns.length > 0">
        <CampaignList
          :campaigns="WhatsAppCampaigns"
          @delete="handleDelete"
          @edit="handleEdit"
          @duplicate="handleDuplicate"
          @view-report="handleViewReport"
          @view-details="handleViewDetails"
        />
      </template>
      <template
        v-else-if="
          (searchTerm || hasActiveStatusFilter || orderBy) &&
          WhatsAppCampaigns.length === 0
        "
      >
        <div class="flex flex-col items-center justify-center py-10 gap-3">
          <Icon icon="i-lucide-search-x" class="text-n-slate-10" size="40" />
          <p class="text-sm text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.NO_RESULTS') }}
          </p>
          <Button
            variant="faded"
            size="sm"
            color="slate"
            label="Limpar filtros"
            @click="clearFilters"
          />
        </div>
      </template>
      <WhatsAppCampaignEmptyState
        v-else
        :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
        :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
        class="pt-14"
      />

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-4 pb-4">
        <PaginationFooter
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="perPage"
          @update:current-page="goToPage"
        />
      </div>
    </main>

    <div v-if="isFilterPanelOpen" class="fixed inset-0 z-50 flex bg-black/20">
      <div class="flex-1" @click="closeFilterPanel" />
      <div
        class="ml-auto bg-white dark:bg-n-solid-2 w-[350px] h-full shadow-xl p-6 flex flex-col"
      >
        <div class="flex items-center justify-between mb-6">
          <span
            class="text-base font-medium text-slate-900 dark:text-n-slate-1"
          >
            {{ t('CAMPAIGN.REPORT.FILTER_BY_STATUS') }}
          </span>
          <button
            class="text-slate-400 dark:text-n-slate-10 hover:text-slate-600 dark:hover:text-n-slate-12 transition-colors"
            @click="closeFilterPanel"
          >
            <Icon icon="i-lucide-x" size="20" />
          </button>
        </div>
        <div class="mb-6">
          <label
            class="block text-sm font-medium mb-1 text-slate-700 dark:text-n-slate-11"
          >
            {{ t('CAMPAIGN.WHATSAPP.ORDER_BY') }}
          </label>
          <div class="relative w-full">
            <select
              v-model="orderBy"
              class="w-full border border-slate-200 dark:border-n-weak rounded px-2 py-1.5 appearance-none pr-8 bg-white dark:bg-n-solid-3 text-slate-700 dark:text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            >
              <option value="">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_DEFAULT') }}
              </option>
              <option value="created_desc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_CREATED_DESC') }}
              </option>
              <option value="created_asc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_CREATED_ASC') }}
              </option>
              <option value="name_asc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_NAME_ASC') }}
              </option>
              <option value="name_desc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_NAME_DESC') }}
              </option>
              <option value="read_desc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_READ_DESC') }}
              </option>
              <option value="read_asc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_READ_ASC') }}
              </option>
              <option value="cancelled_desc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_CANCELLED_DESC') }}
              </option>
              <option value="cancelled_asc">
                {{ t('CAMPAIGN.WHATSAPP.ORDER_CANCELLED_ASC') }}
              </option>
            </select>
            <span
              class="pointer-events-none absolute inset-y-0 right-2 flex items-center text-slate-400 dark:text-n-slate-10"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </span>
          </div>
        </div>
        <div>
          <label
            class="block text-sm font-medium mb-3 text-slate-700 dark:text-n-slate-11"
          >
            {{ t('CAMPAIGN.WHATSAPP.STATUS_LABEL') }}
          </label>
          <div class="flex flex-col gap-2">
            <label class="flex items-center gap-2 mb-3">
              <Checkbox v-model="statusFilters.active" />
              <span
                class="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-xl px-2 py-0.5 text-xs"
              >
                {{ t('CAMPAIGN.WHATSAPP.STATUS.ACTIVE') }}
              </span>
            </label>
            <label class="flex items-center gap-2 mb-3">
              <Checkbox v-model="statusFilters.completed" />
              <span
                class="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-xl px-2 py-0.5 text-xs"
              >
                {{ t('CAMPAIGN.WHATSAPP.STATUS.COMPLETED') }}
              </span>
            </label>
            <label class="flex items-center gap-2 mb-3">
              <Checkbox v-model="statusFilters.draft" />
              <span
                class="bg-slate-100 dark:bg-n-solid-3 text-slate-700 dark:text-n-slate-11 rounded-xl px-2 py-0.5 text-xs"
              >
                {{ t('CAMPAIGN.WHATSAPP.STATUS.DRAFT') }}
              </span>
            </label>
          </div>
        </div>
        <div class="mt-auto pt-6">
          <Button class="w-full" @click="applyFilters">
            {{ t('CAMPAIGN.WHATSAPP.APPLY_FILTERS') }}
          </Button>
        </div>
      </div>
    </div>

    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </div>
</template>
